import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Flex, TreeSelect } from "antd";
import uniq from "lodash/uniq";
import { CSVLink } from "react-csv";
import { getExceptions } from "./reportSlice";
import { HeaderCell } from "../Dashboard/HeaderCell";
import { dateCell } from "../../components/DateCell/DateCell";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import Table from "../../components/SortableTable/SortableTable";
import { IconDownload } from "../../components/IconDownload/IconDownload";
import { MiniCatalogLink } from "../../components/MiniCatalogLink/MiniCatalogLink";
import css from "./Report.module.css";


const warningExceptionStatuses = ['IDENTIFIER_CONFLICT', 'CAT_NO_FORMAT_MISMATCH', 'CAT_NO_MISMATCH'];
const WARNINGS_GROUP_KEY = "group-warnings";
const ERRORS_GROUP_KEY = "group-errors";

export const Exceptions = () => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const { exceptions, getExceptionsInProgress } =
	useSelector((state) => state?.report) || {};

  const [selectedErrors, setSelectedErrors] = useState([]);
  const [hasAppliedDefaultFilter, setHasAppliedDefaultFilter] = useState(false);

  useEffect(() => {
	dispatch(
	  getExceptions(id),
	);
  }, [id]);

  const { warnings, errors } = useMemo(() => {
	const uniqueErrors = uniq((exceptions || []).map((item) => item.validation_error).filter(Boolean));
	return {
	  warnings: uniqueErrors.filter((error) => warningExceptionStatuses.includes(error)),
	  errors: uniqueErrors.filter((error) => !warningExceptionStatuses.includes(error)),
	};
  }, [exceptions]);

  useEffect(() => {
	if (!hasAppliedDefaultFilter && Array.isArray(exceptions) && exceptions.length > 0) {
	  setSelectedErrors(errors);
	  setHasAppliedDefaultFilter(true);
	}
  }, [exceptions, errors, hasAppliedDefaultFilter]);

  const errorTreeData = useMemo(() => ([
	{
	  title: "Warnings",
	  value: WARNINGS_GROUP_KEY,
	  key: WARNINGS_GROUP_KEY,
	  selectable: false,
	  children: warnings.map((error) => ({ title: error, value: error, key: error })),
	},
	{
	  title: "Errors",
	  value: ERRORS_GROUP_KEY,
	  key: ERRORS_GROUP_KEY,
	  selectable: false,
	  children: errors.map((error) => ({ title: error, value: error, key: error })),
	},
  ]), [warnings, errors]);

  const filteredExceptions = useMemo(() => {
	if (!selectedErrors.length) return exceptions || [];
	return (exceptions || []).filter((item) => selectedErrors.includes(item.validation_error));
  }, [exceptions, selectedErrors]);

  const columns = [
	{
	  title: "Exception",
	  dataIndex: "validation_error",
	  sorter: true,
	  align: "left",
	},
	{
	  title: "UPC",
	  dataIndex: "upc",
	  sorter: true,
	  align: "left",
	},
	{
	  title: "Catalog Number",
	  dataIndex: "cat_no",
	  sorter: true,
	  align: "left",
	},
	{
	  title: "Matched Product ID",
	  dataIndex: "product_id",
	  sorter: true,
	  align: "left",
	  /*
	  render: (id) => (
		<MiniCatalogLink id={id} />
	  )
	  */
	},
	{
	  title: "Matched Cat No",
	  dataIndex: "matched_cat_no",
	  sorter: true,
	  align: "left",
	},
	{
	  title: "Primary Format",
	  dataIndex: "primary_format",
	  sorter: true,
	  align: "left",
	},
	{
	  title: "Source",
	  dataIndex: "source",
	  sorter: true,
	  align: "left",
	}



	

	


  ];
  let exportedCSVFile = 'exceptions.csv', pageTitle = '', processedCSVExceptions = [];
  if(Array.isArray(filteredExceptions) && filteredExceptions.length > 0) {
	const sample = filteredExceptions[0];
	exportedCSVFile = `exceptions_${sample.partner}_${sample.report_date.value}.csv`;
	pageTitle = `- ${sample.partner} - ${sample.report_date.value}`;
	
	processedCSVExceptions = filteredExceptions.map((oldRow) => {
		let row = {...oldRow};
		row.report_date = row.report_date && typeof row.report_date === 'object'
		  ? row.report_date.value 
		  : row.report_date;
	
		row.raw_data = typeof row.raw_data === 'string'
		  ? row.raw_data.replace(/"/g, '""') 
		  : row.raw_data;
		return row;
	});  
	
  } 

  return (
	  <>
	  <PageHeader title={`Import Exceptions${pageTitle}`} extraClass={css.reportTitle} />
		<Flex className={css.root} vertical>
		<div className={"ibox-holder"}>

			<TreeSelect
				value={selectedErrors}
				onChange={setSelectedErrors}
				treeData={errorTreeData}
				treeCheckable
				showCheckedStrategy={TreeSelect.SHOW_CHILD}
				treeDefaultExpandAll
				placeholder="All"
				style={{ width: 600, marginBottom: 10 }}
			/>
			
			<CSVLink data={processedCSVExceptions} filename={exportedCSVFile} >
				<IconDownload />
			</CSVLink>


			<Table
				components={{
					header: {
						row: HeaderCell,
					},
				}}
				columns={columns}
				rowKey={(record) => record.id}
				dataSource={filteredExceptions}
				pagination={{
					position: ["bottomRight"],
					responsive: true,
					// onChange: (page) => onQueryParamsChange({ page }),
					// current: page,
					total: filteredExceptions.length,
					pageSize: 10,
					hideOnSinglePage: true,
					showSizeChanger: false,
				}}
				loading={getExceptionsInProgress}
				style={{ marginTop: 10 }}
				scroll={{ x: 'max-content' }}
				className={css.importTable}
			/>
			</div>
		</Flex>

		</>
  );
};